import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from '../../services/auth.service';
import {
  Role,
  UpdateActivePayload,
  UpdateEmailAlertsPayload,
  UpdateProfilePayload,
  UserProfile,
} from '../_models/user.models';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private baseURL = `${environment.apiUrl}/api`;
  private midURL = '/user';
  private PROFILE_URL = '/getprofile';
  private PROFILE_UPDATE_URL = '/updateprofile';
  private APPLICATION_ROLES = '/getapplicationroles';
  private GET_USERS = '/getusers?applicationId=';
  private PAYMENT_URL = '/payment';
  private CURRENT_PLAN_URL = '/currentplan';
  private DEFAULT_PAYMENT_METHOD_URL = '/getDefaultPaymentMethod';
  private ALL_PLANS_URL = '/plans';
  private STRIPE_CUSTOMER_PORTAL_URL = '/create-customer-portal-session';
  private SUBSCRIBE_URL = '/subscribe';
  private ACTIVE_UPDATE_URL = '/updateactive';
  private TOGGLE_EMAIL_ALERT_URL = '/updateemailalert';
  private VALIDATE_EMAIL = '/validateuseremail';
  private UPDATE_EMAIL = '/updateuseremail';
  private BULK_UPDATE = '/bulk';
  private CHECK_USER_EXISTS_URL = '/userExists';

  public get profileUrl() {
    return `${this.baseURL}${this.midURL}${this.PROFILE_URL}`;
  }

  public get profileUpdateUrl() {
    return `${this.baseURL}${this.midURL}${this.PROFILE_UPDATE_URL}`;
  }

  public get applicationRolesUrl() {
    return `${this.baseURL}${this.midURL}${this.APPLICATION_ROLES}`;
  }

  public get getUsersUrl() {
    return `${this.baseURL}${this.midURL}${this.GET_USERS}`;
  }

  public get currentPlanUrl() {
    return `${this.baseURL}${this.PAYMENT_URL}${this.CURRENT_PLAN_URL}`;
  }
  public get defaultPaymentMethodUrl() {
    return `${this.baseURL}${this.PAYMENT_URL}${this.DEFAULT_PAYMENT_METHOD_URL}`;
  }

  public get allPlansUrl() {
    return `${this.baseURL}${this.PAYMENT_URL}${this.ALL_PLANS_URL}`;
  }

  public get stripeCustomerPortalUrl() {
    return `${this.baseURL}${this.midURL}${this.STRIPE_CUSTOMER_PORTAL_URL}`;
  }

  public get subscribeUrl() {
    return `${this.baseURL}${this.PAYMENT_URL}${this.SUBSCRIBE_URL}`;
  }

  public get activeUrl() {
    return `${this.baseURL}${this.midURL}${this.ACTIVE_UPDATE_URL}`;
  }

  public get emailAlertUrl() {
    return `${this.baseURL}${this.midURL}${this.TOGGLE_EMAIL_ALERT_URL}`;
  }
  public get validateEmailUrl() {
    return `${this.baseURL}${this.midURL}${this.VALIDATE_EMAIL}`;
  }
  public get updateEmailUrl() {
    return `${this.baseURL}${this.midURL}${this.UPDATE_EMAIL}`;
  }
  public get bulkUpdateUrl() {
    return `${this.baseURL}${this.midURL}${this.BULK_UPDATE}`;
  }

  public get checkUserUrl() {
    return `${this.baseURL}${this.midURL}${this.CHECK_USER_EXISTS_URL}`;
  }

  constructor(public http: HttpClient, private authService: AuthService) {}

  /**
   * 
   * Update user active status
   */
  updateActive = (payload: UpdateActivePayload): Observable<UserProfile> =>
    this.http.put<UserProfile>(`${this.activeUrl}`, payload, {});

    /**
   * 
   * Update user email alerts status
   */
  toggleEmailAlerts = (payload: UpdateEmailAlertsPayload): Observable<UserProfile> =>
  this.http.put<UserProfile>(`${this.emailAlertUrl}`, payload, {});
    
  /**
   * Current user profile
   * 
   * @param applicationKey Optional application key to get entity-scoped role.
   *   IMPORTANT: Caller MUST check ENABLE_ENTITY_LEVEL_TOP_LEVEL_ROLE feature flag
   *   before passing applicationKey. The service trusts the caller to handle feature flag checks.
   *   See user.effects.ts for proper feature flag implementation.
   */
  getProfile = (applicationKey?: string): Observable<UserProfile> => {
    // Backwards compatible: only add query param if applicationKey is provided
    const url = applicationKey
      ? `${this.profileUrl}?applicationKey=${applicationKey}`
      : this.profileUrl;
    return this.http.get<UserProfile>(url, {});
  };

  /**
   * Current user profile
   */
  updateProfile = (payload: UpdateProfilePayload): Observable<UserProfile> =>
    this.http.post<UserProfile>(`${this.profileUpdateUrl}`, payload, {});

  /**
   * Get application roles
   */
  applicationRoles = (applicationId: number,tableId: string='', fromForm:boolean=false): Observable<Array<Role>> =>
    this.http.get<Array<Role>>(`${this.applicationRolesUrl}?applicationId=${applicationId}&tableId=${tableId}&fromForm=${fromForm}`, {
      headers: this.authService.getHeaders(),
    });

  /**
   * Get uses of application id
   */
  getUsers = (
    applicationId: number,
    isSearch: boolean,
    search: string,
    isScroll: boolean,
    skip: number,
    pageSize: number,
    isOptimizedComponent: boolean
  ): Observable<Array<UserProfile>> =>
    this.http.get<Array<UserProfile>>(
      `${this.getUsersUrl}${applicationId}&isSearch=${isSearch}&search=${encodeURIComponent(search)}&isScroll=${isScroll}&skip=${skip}&pageSize=${pageSize}&isOptimizedComponent=${isOptimizedComponent}`
    );

  /**
   * Get current subscription plans
   */
  getCurrentPlan = (): Observable<any> =>
    this.http.get<any>(`${this.currentPlanUrl}`);

  /**
   * Get all subscription plans
   */
  getAllPlans = (): Observable<any> =>
    this.http.get<any>(`${this.allPlansUrl}`);

  /**
   * Get default payment method
   */
  getDefaultPaymentMethod = (customerId: any): Observable<any> =>
    this.http.get<any>(
      `${this.defaultPaymentMethodUrl}?customerId=${customerId}`
    );

  /**
   * Get stripe customer portal link
   */
  getStripeCustomerPortal = (): Observable<any> =>
    this.http.get<any>(`${this.stripeCustomerPortalUrl}`);

  /**
   * Subscribe Plan
   */
  subscribePlan = (): Observable<any> =>
    this.http.post<any>(`${this.subscribeUrl}`, {});

  validateUserEmail = (payload:any): Observable<any> =>
    this.http.post<any>(`${this.validateEmailUrl}`, payload);

  updateUserEmail = (payload:any): Observable<any> =>
    this.http.post<any>(`${this.updateEmailUrl}`, payload);

  bulkUpdateUsers = (payload:any): Observable<any> =>
    this.http.put<any>(`${this.bulkUpdateUrl}`, payload);

  /**
   * Check if User Exists in GAB IDP
   * Backend returns: { userExists: true/false }
   * 
   * @param payload - { Email: string, ApplicationKey: string }
   */
  checkIfUserExists = (payload: { Email: string; ApplicationKey: string }): Observable<{ userExists: boolean }> =>
    this.http.post<{ userExists: boolean }>(`${this.checkUserUrl}`, payload);
}
